import { useCallback, useEffect, useMemo, useRef } from 'react';
import { logger } from '~/scripts/logger';

interface Event extends Record<string, unknown> {
  event: string;
}

export default function useDataLayer() {
  // Use a ref to store the queue of events
  const eventQueueRef = useRef<Event[]>([]);

  // Function to push events
  const pushToDataLayer = useCallback((event: Event) => {
    // If window is available (client-side)
    if (typeof window !== 'undefined') {
      // If queue has pending events, push them first
      while (eventQueueRef.current.length > 0) {
        const queuedEvent = eventQueueRef.current.shift();
        if (queuedEvent) {
          window.dataLayer.push(queuedEvent);
        }
      }

      // Push the current event
      window.dataLayer.push(event);
      logger.debug('[dataLayer]', event);
    } else {
      // If server-side, enqueue the event
      logger.debug('[dataLayer]', event, 'server');
      eventQueueRef.current.push(event);
    }
  }, []);

  // Use effect to handle initial queue push on client-side mount
  useEffect(() => {
    // Only run on client-side
    if (typeof window !== 'undefined') {
      // Push any queued events
      while (eventQueueRef.current.length > 0) {
        const event = eventQueueRef.current.shift();
        if (event) {
          window.dataLayer.push(event);
        }
      }
    }
  }, []); // Empty dependency array ensures this runs only on mount

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(() => ({ push: pushToDataLayer }), [pushToDataLayer]);
}
